/**
 * Wizard
 */
class Wizard
{
    /**
	 * Wizard constructor
	 * @param selector {string}
	 * @param options {Object}
	 */
    constructor(selector, options = {})
    {
        // selectors
        this.wizardSelector = selector;
        this.progressSelector = '.js-wizard__progress';
        this.stepSelector = '.js-wizard__step';
        this.prevButtonSelector = '.js-wizard__prev';
        this.nextButtonSelector = '.js-wizard__next';
        this.formSelector = 'form';

        // i18n data attributes
        this.dataNextTextIndex = 'wizardNextText';
        this.dataPrevTextIndex = 'wizardPrevText';

        // elements
        this.wizard = document.querySelector(this.wizardSelector);
        this.steps = null;
        this.progress = null;
        this.form = null;

        // options
        this.options = Object.assign({
            buttonClass: ['button'],
            hiddenClass: 'hidden',
            skipStep: [],
            validate: false,
            urlHashPrefix: 'wizard-step',
            stepConfig: {}
        }, options);

        // initials
        this.initials = {
            currentStep: 0,
            stepCount: 0,
        };

        this.defaultStepConfig = {
            prevButton: {
                visible: true,
                classes: []
            },
            nextButton: {
                visible: true,
                classes: []
            },
        };
    }

    /**
	 * Init wizard
	 */
    init()
    {
        if (this.wizard === null)
        {
            return;
        }

        this.initials.stepCount = this.fetchStepCount();
        this.steps = this.fetchAllSteps();
        this.progress = this.fetchProgress();
        this.form = this.fetchForm();

        this.steps.forEach((stepEl, stepIndex) =>
        {
            // objects are references, so we need to make a deep clone to not change the origin with Object.assign
            this.defaultStepConfigClone = JSON.parse(JSON.stringify(this.defaultStepConfig));

            if (typeof this.options.stepConfig[stepIndex] === 'undefined')
            {
                this.options.stepConfig[stepIndex] = this.defaultStepConfigClone;
            }
            else
            {
                this.options.stepConfig[stepIndex].prevButton = Object.assign(
                    this.defaultStepConfigClone.prevButton,
                    this.options.stepConfig[stepIndex].prevButton
                );
                this.options.stepConfig[stepIndex].nextButton = Object.assign(
                    this.defaultStepConfigClone.nextButton,
                    this.options.stepConfig[stepIndex].nextButton
                );
            }

            this.addButtons(stepEl, stepIndex);
        });

        if (this.isUrlParameterAvailable())
        {
            this.initials.currentStep = this.fetchStepFromUrlParameter();
        }

        this.initEvents();

        this.goto(this.initials.currentStep);
    }

    initEvents()
    {
        this.wizard.addEventListener(orion.constants.EventWizardGoto, this.handleEventGoto.bind(this));
    }

    /**
	 * Trigger and check form validation before going to the next step.
	 * All forms can be validated by triggering the EventFormValidate event on the form element.
	 * @param event {EventTarget}
	 */
    next(event)
    {
        event.preventDefault();

        if (this.options.validate)
        {
            this.form.dispatchEvent(new Event(orion.constants.EventFormValidate));
        }

        let nextStep = this.calcNextStep(this.initials.currentStep);

        /*
		 * let nextStep = this.initials.currentStep + 1 >= (this.initials.stepCount - 1)
		 * 	? (this.initials.stepCount - 1) : this.initials.currentStep + 1;
		 */

        if (nextStep !== false && nextStep !== this.initials.currentStep && this.isCurrentStepValid())
        {
            let nextBeforeEvent = new CustomEvent(
                orion.constants.EventWizardStepNextBefore,
                { detail: { currentStep: nextStep - 1 } }
            );

            this.wizard.dispatchEvent(nextBeforeEvent);

            this.goto(nextStep);

            let nextAfterEvent = new CustomEvent(
                orion.constants.EventWizardStepNextAfter,
                { detail: { currentStep: nextStep } }
            );

            this.wizard.dispatchEvent(nextAfterEvent);
        }
    }

    /**
	 * @param event {EventTarget}
	 */
    prev(event)
    {
        event.preventDefault();

        let prevStep = this.calcPrevStep(this.initials.currentStep);
        // let prevStep = this.initials.currentStep - 1 <= 0 ? 0 : this.initials.currentStep - 1;

        if (prevStep !== false && prevStep !== this.initials.currentStep)
        {
            let prevBeforeEvent = new CustomEvent(
                orion.constants.EventWizardStepPrevBefore,
                { detail: { currentStep: prevStep + 1 } }
            );

            this.wizard.dispatchEvent(prevBeforeEvent);

            this.goto(prevStep);

            let prevAfterEvent = new CustomEvent(
                orion.constants.EventWizardStepPrevAfter,
                { detail: { currentStep: prevStep } }
            );

            this.wizard.dispatchEvent(prevAfterEvent);
        }
    }

    /**
	 * @param stepIndex {number}
	 */
    goto(stepIndex)
    {
        this.initials.currentStep = stepIndex;

        this.steps.forEach((stepEl) =>
        {
            stepEl.classList.add(this.options.hiddenClass);
        });

        let currentStep = this.steps[stepIndex];

        currentStep.classList.remove(this.options.hiddenClass);

        this.focusFirstInputInStep(currentStep);

        if (this.progress.length)
        {
            this.updateProgress(stepIndex);
        }

        this.wizard.scrollIntoView();

        if (this.options.validate)
        {
            this.form.dispatchEvent(new Event(orion.constants.EventFormInitNext));
        }
    }

    /**
	 * @returns {boolean}
	 */
    isCurrentStepValid()
    {
        return this.steps[this.initials.currentStep].querySelectorAll('div.error').length === 0;
    }

    /**
	 * @param stepIndex {number}
	 */
    updateProgress(stepIndex)
    {
        this.progress.forEach((progressEl, index) =>
        {
            if (index < stepIndex)
            {
                progressEl.classList.add('done');
            }
            else
            {
                progressEl.classList.remove('done');
            }

            progressEl.classList.remove('current');
        });

        this.progress[stepIndex].classList.add('current');
    }

    /**
	 * @returns {number}
	 */
    fetchStepCount()
    {
        return document.querySelectorAll(this.stepSelector).length;
    }

    /**
	 * @param text {string}
	 * @param cb {function}
	 * @returns {HTMLButtonElement}
	 */
    generateButton(text, classes, cb)
    {
        let button = document.createElement('button');
        let mergedClasses = this.options.buttonClass.concat(classes);

        button.classList.add(...mergedClasses);
        button.textContent = text;

        if (typeof cb === 'function')
        {
            button.addEventListener('click', cb.bind(this), true);
        }

        return button;
    }

    /**
	 * @returns {HTMLButtonElement}
	 */
    generatePrevButton(classes)
    {
        let prevText = this.fetchPrevButtonText();

        return this.generateButton(prevText, classes, this.prev);
    }

    /**
	 * @returns {HTMLButtonElement}
	 */
    generateNextButton(classes)
    {
        let nextText = this.fetchNextButtonText();

        return this.generateButton(nextText, classes, this.next);
    }

    /**
	 * @param stepEl {HTMLUListElement}
	 * @param stepIndex {number}
	 */
    addButtons(stepEl, stepIndex)
    {
        if (this.options.skipStep.indexOf(stepIndex) !== -1)
        {
            return;
        }

        let nextStep = this.calcNextStep(stepIndex);
        let prevStep = this.calcPrevStep(stepIndex);

        if (prevStep !== false && this.options.stepConfig[stepIndex].prevButton.visible)
        {
            stepEl.appendChild(this.generatePrevButton(this.options.stepConfig[stepIndex].prevButton.classes));
        }

        if (nextStep !== false && this.options.stepConfig[stepIndex].nextButton.visible)
        {
            stepEl.appendChild(this.generateNextButton(this.options.stepConfig[stepIndex].nextButton.classes));
        }
    }

    /**
	 * @param currentStepIndex
	 * @returns {*}
	 */
    calcNextStep(currentStepIndex)
    {
        let nextStep = currentStepIndex + 1;

        while (this.options.skipStep.indexOf(nextStep) !== -1)
        {
            nextStep++;
        }

        if (nextStep > this.initials.stepCount - 1)
        {
            nextStep = false;
        }

        return nextStep;
    }

    /**
	 * @param currentStepIndex
	 * @returns {*}
	 */
    calcPrevStep(currentStepIndex)
    {
        let prevStep = currentStepIndex - 1;

        while (this.options.skipStep.indexOf(prevStep) !== -1)
        {
            prevStep--;
        }

        if (prevStep < 0)
        {
            prevStep = false;
        }

        return prevStep;
    }

    /**
	 * @returns {NodeListOf<HTMLElementTagNameMap[string]> | NodeListOf<Element> | NodeListOf<SVGElementTagNameMap[string]>}
	 */
    fetchAllSteps()
    {
        return this.wizard.querySelectorAll(this.stepSelector);
    }

    /**
	 * @returns {NodeListOf<HTMLElementTagNameMap[string]> | NodeListOf<Element> | NodeListOf<SVGElementTagNameMap[string]>}
	 */
    fetchProgress()
    {
        return this.wizard.querySelectorAll(this.progressSelector + ' li');
    }

    /**
	 * @returns {NodeListOf<HTMLElementTagNameMap[string]> | NodeListOf<Element> | NodeListOf<SVGElementTagNameMap[string]>}
	 */
    fetchForm()
    {
        return this.wizard.querySelector(this.formSelector);
    }

    /**
	 * @returns {string}
	 */
    fetchPrevButtonText()
    {
        return this.wizard.dataset[this.dataPrevTextIndex] || 'previous';
    }

    /**
	 * @returns {string}
	 */
    fetchNextButtonText()
    {
        return this.wizard.dataset[this.dataNextTextIndex] || 'next';
    }

    /**
	 * @returns {boolean}
	 */
    isUrlParameterAvailable()
    {
        return window.location.search.indexOf(this.options.urlHashPrefix) !== -1;
    }

    /**
	 * @returns {number}
	 */
    fetchStepFromUrlParameter()
    {
        let query = window.location.search;

        let regexPattern = this.options.urlHashPrefix.replace('-', '\\-') + '[=\\-]+(\\d+)';
        let regex = new RegExp(regexPattern);

        let stepIndexMatch = query.match(regex);

        let stepIndex = this.initials.currentStep;

        if (stepIndexMatch)
        {
            [, stepIndex] = stepIndexMatch;
        }

        if (!this.isValidStepIndex(stepIndex))
        {
            stepIndex = this.initials.currentStep;
        }

        return parseInt(stepIndex, 10);
    }

    isValidStepIndex(index)
    {
        return index >= 0 && index <= this.initials.stepCount;
    }

    focusFirstInputInStep(stepElement)
    {
        let input = stepElement.querySelector('input');

        if (input !== null)
        {
            input.focus();
        }
    }

    handleEventGoto(event)
    {
        if (typeof event.detail.step === 'undefined')
        {
            return;
        }

        let stepIndex = event.detail.step;

        this.goto(stepIndex);
    }
}

let wizard = new Wizard('.js-wizard', {
    skipStep: [6],
    validate: true,
    buttonClass: ['button', 'back-more__button'],
    stepConfig: {
        0: {
            nextButton: {
                classes: ['primary']
            }
        },
        1: {
            prevButton: {
                classes: ['tertiary']
            },
            nextButton: {
                classes: ['primary']
            }
        },
        2: {
            prevButton: {
                classes: ['tertiary']
            },
            nextButton: {
                classes: ['primary']
            }
        },
        3: {
            prevButton: {
                classes: ['tertiary']
            },
            nextButton: {
                classes: ['primary']
            }
        },
        4: {
            prevButton: {
                classes: ['tertiary']
            },
            nextButton: {
                classes: ['primary']
            }
        },
        5: {
            prevButton: {
                classes: ['tertiary']
            },
            nextButton: {
                classes: ['tertiary']
            }
        },
        6: {
            prevButton: {
                classes: ['tertiary']
            },
            nextButton: {
                classes: ['tertiary']
            }
        },
    }
});

wizard.init();
